export type Role = 'controller' | 'controllee'
export type ControlState = 'idle' | 'requested' | 'pending' | 'active' | 'ended'

export interface ControlSession {
  state: ControlState
  sessionId: string | null
  role: Role | null
  peerUserId: string | null
  peerName: string | null
  /** LiveKit identity of the controller — the controllee gates input by this. */
  controllerIdentity: string | null
  endReason: string | null
}

export const initialSession: ControlSession = {
  state: 'idle',
  sessionId: null,
  role: null,
  peerUserId: null,
  peerName: null,
  controllerIdentity: null,
  endReason: null,
}

interface Pairing {
  sessionId: string
  peerUserId: string
  peerName: string
  controllerIdentity: string
}

export type ControlEvent =
  | ({ type: 'REQUEST' } & Pairing)          // this client initiates control (controller)
  | ({ type: 'INCOMING_REQUEST' } & Pairing) // a peer requests to control this client (controllee)
  | { type: 'APPROVED' }
  | { type: 'DENIED' }
  | { type: 'END'; reason: string }

export function reduce(s: ControlSession, e: ControlEvent): ControlSession {
  switch (e.type) {
    case 'REQUEST':
      if (s.state !== 'idle') return s
      return {
        ...initialSession,
        state: 'requested',
        role: 'controller',
        sessionId: e.sessionId,
        peerUserId: e.peerUserId,
        peerName: e.peerName,
        controllerIdentity: e.controllerIdentity,
      }
    case 'INCOMING_REQUEST':
      if (s.state !== 'idle') return s
      return {
        ...initialSession,
        state: 'pending',
        role: 'controllee',
        sessionId: e.sessionId,
        peerUserId: e.peerUserId,
        peerName: e.peerName,
        controllerIdentity: e.controllerIdentity,
      }
    case 'APPROVED':
      if (s.state !== 'requested' && s.state !== 'pending') return s
      return { ...s, state: 'active' }
    case 'DENIED':
      if (s.state !== 'requested' && s.state !== 'pending') return s
      return { ...s, state: 'ended', endReason: 'denied' }
    case 'END':
      if (s.state === 'idle') return s
      return { ...s, state: 'ended', endReason: e.reason }
    default:
      return s
  }
}
